import { useEffect, useRef, useState } from 'react';
import Icon, { ICONS } from './icons';

interface CopyValueButtonProps {
  value: string;
  title?: string;
  copiedTitle?: string;
  className?: string;
}

const COPIED_STATE_DURATION_MS = 1_500;

export default function CopyValueButton({
  value,
  title,
  copiedTitle,
  className = '',
}: CopyValueButtonProps) {
  const [copied, setCopied] = useState(false);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (resetTimeoutRef.current !== null) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      if (!mountedRef.current) return;
      setCopied(true);

      if (resetTimeoutRef.current !== null) {
        clearTimeout(resetTimeoutRef.current);
      }
      resetTimeoutRef.current = setTimeout(() => {
        setCopied(false);
        resetTimeoutRef.current = null;
      }, COPIED_STATE_DURATION_MS);
    } catch {
      // Clipboard access can be unavailable or denied; keep the button retryable.
    }
  };

  return (
    <button
      type="button"
      title={copied ? (copiedTitle ?? title) : title}
      aria-label={copied ? (copiedTitle ?? title) : title}
      className={`inline-flex items-center justify-center rounded p-1 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white ${className}`.trim()}
      onClick={() => void handleCopy()}
    >
      <Icon name={copied ? ICONS.CHECK : ICONS.COPY} size={14} />
    </button>
  );
}
